import { Card, Grid, Stack, Typography } from '@mui/material';
import { DataGrid, deDE } from '@mui/x-data-grid';
import { IAsset } from '../../../../../Interfaces/Asset/IAsset';
import {
  IAssetCategoryId,
  IAssetCategoryName,
} from '../../../../../Interfaces/Asset/IAssetCategory';
import { isInheritanceProvisionRelationship } from '../../../../../Interfaces/ILegalTransactionRelationship/Relationships/InheritanceProvision/ILegalTransactionRelationshipInheritanceProvision';
import { ILegalTransactionRelationship } from '../../../../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationship';
import { useLegalClientStore } from '../../generic/legalClientStore';
import { present } from '../../generic/utilities';

export type ITestamentAssetsSummaryProps = {
  assetArray: IAsset[];
  relationshipArray: ILegalTransactionRelationship[];
};

export function TestamentAssetSummary({
  asset,
  relationshipArray,
}: {
  asset: IAsset;
  relationshipArray: ILegalTransactionRelationship[];
}) {
  const [legalClientArray] = useLegalClientStore(state => [
    state.legalClientArray,
  ]);

  const inheritanceProvisionRelationship = relationshipArray
    .filter(isInheritanceProvisionRelationship)
    .find(relationship =>
      relationship.AssetArray.some(
        relAsset => relAsset.idAsset === asset.idAsset
      )
    );

  const inheritors = inheritanceProvisionRelationship
    ? inheritanceProvisionRelationship.Members.filter(member => member.data)
        .map(member =>
          legalClientArray.find(lc => lc.idLegalClient === member.idLegalClient)
        )
        .filter(present)
    : [];

  const assetProvisions =
    inheritanceProvisionRelationship?.data.provisionNotes || '';

  const owners = legalClientArray.filter(lc =>
    lc.AssetArray.some(relAsset => relAsset.idAsset === asset.idAsset)
  );

  return (
    <Card sx={{ p: 2 }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant='h6'>
            {asset.name || 'Unbenannter Vermögenswert'}{' '}
            {owners.length > 0 && (
              <span style={{ fontWeight: 'normal', fontSize: '0.8em' }}>
                (Eigentümer:{' '}
                {owners
                  .map(owner => `${owner.FirstName} ${owner.LastName}`)
                  .join(' und ')}
                )
              </span>
            )}
          </Typography>
          {assetProvisions && (
            <>
              <Typography sx={{ mt: 1 }}>Bestimmungen</Typography>
              <Card
                sx={{
                  p: 2,
                  mt: 1,
                }}
              >
                {assetProvisions}
              </Card>
            </>
          )}
        </Grid>
        <Grid item xs={12}>
          <Typography variant='subtitle1'>Erben</Typography>
          <DataGrid
            rows={inheritors}
            columns={[
              {
                field: 'fullName',
                headerName: 'Name',
                flex: 1,
                valueGetter: params =>
                  `${params.row.FirstName || ''} ${params.row.LastName || ''}`,
              },
              {
                field: 'legalClientProvisions',
                headerName: 'Bestimmungsnotiz',
                flex: 2,
                valueGetter: params => {
                  const member = inheritanceProvisionRelationship?.Members.find(
                    m => m.idLegalClient === params.row.idLegalClient
                  );

                  if (!member) {
                    return '';
                  }

                  return member.data.provisionNotes || '';
                },
              },
            ]}
            getRowId={row => row.idLegalClient}
            autoHeight
            localeText={deDE.components.MuiDataGrid.defaultProps.localeText}
            disableRowSelectionOnClick
          />
        </Grid>
      </Grid>
    </Card>
  );
}

export function TestamentAssetCategorySummary({
  idAssetCategory,
  assetsInCategory,
  relationshipArray,
}: {
  idAssetCategory: IAssetCategoryId;
  assetsInCategory: IAsset[];
  relationshipArray: ILegalTransactionRelationship[];
}) {
  return (
    <Grid item xs={12}>
      <Grid container>
        <Grid item xs={12}>
          <Typography variant='h5' sx={{ mb: 2 }}>
            {IAssetCategoryName[idAssetCategory]}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Stack spacing={3}>
            {assetsInCategory.map(asset => (
              <TestamentAssetSummary
                key={asset.idAsset}
                asset={asset}
                relationshipArray={relationshipArray}
              />
            ))}
          </Stack>
        </Grid>
      </Grid>
    </Grid>
  );
}

function groupBy<
  T extends Record<string | number | symbol, unknown>,
  K extends keyof T,
>(array: T[], getKey: (item: T) => K): Record<K, T[]> {
  return array.reduce(
    (result, item) => {
      const key = getKey(item);
      if (!result[key]) {
        result[key] = [];
      }
      result[key].push(item);
      return result;
    },
    {} as Record<K, T[]>
  );
}

export function TestamentAssetsSummary({
  assetArray,
  relationshipArray,
}: ITestamentAssetsSummaryProps) {
  const assetsByCategory = groupBy(assetArray, () => 'idAssetCategory');

  return (
    <Grid container>
      <Grid item xs={12}>
        <Typography variant='h4' sx={{ mb: 2 }}>
          Übersicht der Vermögenswerte
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={4}>
          {Object.entries(assetsByCategory).map(
            ([idAssetCategory, assetsInCategory]) => (
              <TestamentAssetCategorySummary
                key={idAssetCategory}
                idAssetCategory={Number(idAssetCategory) as IAssetCategoryId}
                assetsInCategory={assetsInCategory}
                relationshipArray={relationshipArray}
              />
            )
          )}
        </Grid>
      </Grid>
    </Grid>
  );
}
